import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Cpu,
  Wifi,
  Gauge,
  Activity,
  Zap,
  BatteryCharging,
  Thermometer,
  Hash,
  Plus,
  Pencil,
  Trash2,
  Power,
  ScrollText,
  Info,
  BellOff,
  type LucideIcon,
} from 'lucide-react';
import { App } from 'antd';
import { dayjs } from '@/lib/dayjs';
import { showToast } from '@/lib/toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAcceptBoardDeviceUid, useIgnoreBoardDeviceUid, useBoard, useDeleteBoard, useUpdateBoard } from '@/features/boards/hooks';
import { getBoardLiveStatus } from '@/features/boards/api';
import { RelayPanel } from './RelayPanel';
import { useBoardSensors, useCreateSensor, useDeleteSensor, useUpdateSensor } from '@/features/sensors/hooks';
import { useAdminSites } from '@/features/sites/adminHooks';
import { useSiteRealtime } from '@/features/realtime/realtimeStore';
import type {
  BoardWithSensors,
  CreateSensorPayload,
  SensorWithContext,
  TelemetryPoint,
  UpdateBoardPayload,
  UpdateSensorPayload,
} from '@monitor/shared';
import { SensorFormModal } from './SensorFormModal';
import { BoardFormModal } from './BoardFormModal';
import { EnergyHistory } from './EnergyHistory';
import { ActivityHistory } from './ActivityHistory';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { boardStatusOf, BOARD_STATUS_META, mqttDisplayState, MQTT_STATE_META } from '@/lib/boardStatus';
import { BoardStatusBadge } from '@/components/BoardStatusBadge';
import { diagnoseBoard, ageText } from '@/lib/boardDiagnosis';
import { BoardDiagnosisModal, type DiagnosisFact } from '@/components/BoardDiagnosisModal';
import { useAlerts } from '@/features/alerts/hooks';

export function BoardDetailPage() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { modal } = App.useApp();
  const id = boardId ? Number(boardId) : null;

  const { data: board, isLoading } = useBoard(id);
  const { data: sensors = [] } = useBoardSensors(id);

  // Realtime is subscribed globally in App.tsx — we just read the store here.
  const siteState = useSiteRealtime(board?.siteId ?? 0);

  const createMut = useCreateSensor();
  const updateMut = useUpdateSensor();
  const deleteMut = useDeleteSensor();
  const updateBoardMut = useUpdateBoard();
  const deleteBoardMut = useDeleteBoard();
  const acceptUidMut = useAcceptBoardDeviceUid();
  const ignoreUidMut = useIgnoreBoardDeviceUid();
  const { data: sites = [] } = useAdminSites();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SensorWithContext | null>(null);
  const [deletingSensor, setDeletingSensor] = useState<SensorWithContext | null>(null);
  const [boardEditOpen, setBoardEditOpen] = useState(false);
  const [boardDeleting, setBoardDeleting] = useState(false);
  // Persist the active tab in the URL (?tab=…) so a refresh / shared link keeps
  // you on the same tab instead of snapping back to "เซนเซอร์".
  const [searchParams, setSearchParams] = useSearchParams();
  const TAB_KEYS = ['energy', 'control', 'activity', 'info'] as const;
  type TabKey = (typeof TAB_KEYS)[number];
  const urlTab = searchParams.get('tab');
  const tab: TabKey = (TAB_KEYS as readonly string[]).includes(urlTab ?? '') ? (urlTab as TabKey) : 'info';
  const setTab = (t: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  };
  const { data: live } = useQuery({
    queryKey: ['board-live', id],
    queryFn: () => getBoardLiveStatus(id!),
    enabled: id != null,
    refetchInterval: 5000,
  });
  // Open alerts for the board's site (filtered to this board below) feed the
  // click-to-diagnose popup with no_signal / offline / threshold reasons.
  const { data: siteAlerts = [] } = useAlerts(board?.siteId ?? null);
  const [diagOpen, setDiagOpen] = useState(false);
  if (isLoading) {
    return <div style={{ padding: 20, color: 'var(--dim)' }}>กำลังโหลด...</div>;
  }
  if (!board) {
    return <div style={{ padding: 20, color: 'var(--red)' }}>ไม่พบบอร์ด</div>;
  }

  // Map sensorId -> latest TelemetryPoint, sourced from the WebSocket store.
  const latestBySensor = new Map<number, TelemetryPoint>();
  for (const s of sensors) {
    const point = siteState?.bySensor?.[s.id]?.latest;
    if (point) latestBySensor.set(s.id, point);
  }
  // Online = recent activity from EITHER the persisted lastSeenAt OR a live
  // telemetry point (the board record isn't refetched, so relying on lastSeenAt
  // alone made the badge go stale-Offline while sensors were clearly LIVE).
  let newestActivity = board.lastSeenAt ? dayjs(board.lastSeenAt).valueOf() : 0;
  for (const p of latestBySensor.values()) {
    if (p.time) newestActivity = Math.max(newestActivity, dayjs(p.time).valueOf());
  }
  // Shared board-status logic (broker presence first, telemetry-freshness
  // fallback). `newestActivity` folds in the live WebSocket point so the badge
  // stays fresh even though the board record isn't refetched. `live` carries
  // the freshest broker presence, so it takes priority over board.brokerConnected.
  const boardConnStatus = boardStatusOf(
    { brokerConnected: live?.brokerConnected ?? board.brokerConnected, lastSeenAt: board.lastSeenAt },
    newestActivity > 0 ? newestActivity : null,
  );
  const isOnline = boardConnStatus === 'online';

  // Diagnosis for the click-to-explain status popup.
  const boardAlerts = siteAlerts.filter((a) => a.boardId === board.id);
  const diagnosis = diagnoseBoard({
    isActive: board.isActive,
    brokerConnected: live?.brokerConnected ?? board.brokerConnected,
    lastSeenAt: board.lastSeenAt,
    extraActivityMs: newestActivity > 0 ? newestActivity : null,
    pendingDeviceUid: board.pendingDeviceUid ?? null,
    alerts: boardAlerts.map((a) => ({ code: a.code, message: a.message, severity: a.severity })),
  });
  const brokerState = live?.brokerConnected ?? board.brokerConnected;
  // ONE MQTT verdict shared by every chip/field/popup on this page — so the page
  // can't contradict itself (or the index). Fresh telemetry counts as connected;
  // a stale board with unknown broker presence is 'ไม่ทราบ', never a fake "เชื่อมต่อ".
  const mqttState = mqttDisplayState(brokerState, newestActivity > 0 ? newestActivity : null);
  const mqttMeta = MQTT_STATE_META[mqttState];
  const diagFacts: DiagnosisFact[] = [
    { label: 'ข้อมูลล่าสุด', value: newestActivity > 0 ? `${ageText(newestActivity)}ที่แล้ว` : 'ไม่เคยส่ง' },
    { label: 'MQTT', value: mqttMeta.label },
    { label: 'Firmware', value: live?.firmware ?? board.firmware ?? '-' },
    { label: 'IP', value: live?.ipAddress ?? board.ipAddress ?? '-' },
    { label: 'Device UID', value: board.deviceUid ?? live?.deviceUid ?? '-' },
    { label: 'ใช้งาน', value: board.isActive ? 'เปิด' : 'ปิด' },
  ];
  // Relay control is only available on firmware v0.16+; hide the tab otherwise.
  const relayCapable = fwSupportsRelays(live?.firmware ?? board.firmware);
  const showTab = !relayCapable && tab === 'control' ? 'energy' : tab;

  // Slot status: 1 board may hold at most 1 PZEM + 1 KWS
  const hasPzem = sensors.some((s) => (s.model ?? '').toLowerCase().startsWith('pzem'));
  const hasKws = sensors.some((s) => (s.model ?? '').toLowerCase().startsWith('kws'));
  const slotsFull = hasPzem && hasKws;

  // Live connectivity, derived once and shared by the always-visible status
  // strip and the "ข้อมูลบอร์ด" tab. Broker presence is authoritative; the
  // board's self-reported mqttUp is only a fallback.
  const showLive = relayCapable && !!live;
  const transport = live?.transport;
  const linkLabel = transport === 'ethernet' ? 'Ethernet' : transport === 'wifi' ? 'WiFi' : 'Offline';
  const linkColor = transport === 'ethernet' ? '#22c55e' : transport === 'wifi' ? '#22d3ee' : '#ef4444';
  const linkDetail =
    transport === 'ethernet'
      ? (live?.linkMbps ? `${live.linkMbps} Mbps` : '')
      : transport === 'wifi'
        ? (live?.rssi != null ? `${live.rssi} dBm` : '')
        : '';

  const handleSubmit = async (payload: CreateSensorPayload | UpdateSensorPayload) => {
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, payload });
        showToast(`อัปเดต ${editing.code} เรียบร้อย`);
      } else {
        await createMut.mutateAsync(payload as CreateSensorPayload);
        showToast('เพิ่มเซ็นเซอร์เรียบร้อย');
      }
      setModalOpen(false);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        (err as Error).message ??
        'บันทึกไม่สำเร็จ';
      showToast(msg, 'error');
    }
  };

  const handleToggle = async (s: SensorWithContext) => {
    try {
      await updateMut.mutateAsync({ id: s.id, payload: { isActive: !s.isActive } });
      showToast(s.isActive ? `ปิด ${s.code}` : `เปิด ${s.code}`, 'info');
    } catch (err) {
      showToast((err as Error).message ?? 'เปลี่ยนสถานะไม่สำเร็จ', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deletingSensor) return;
    try {
      await deleteMut.mutateAsync(deletingSensor.id);
      showToast(`ลบ ${deletingSensor.code} แล้ว`, 'info');
    } catch (err) {
      showToast((err as Error).message ?? 'ลบไม่สำเร็จ', 'error');
    }
    setDeletingSensor(null);
  };

  // Accept a quarantined foreign device (duplicate board_code). Guarded by a
  // confirm modal — this REBINDS the board to the new device and starts trusting
  // its data, so it must be a deliberate admin action.
  const pending = board.pendingDeviceUid ?? null;
  const handleAcceptUid = () => {
    if (!pending || id == null) return;
    modal.confirm({
      title: 'ยอมรับอุปกรณ์ใหม่?',
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          บอร์ด <b>{board.code}</b> จะเปลี่ยนไปผูกกับอุปกรณ์ <b>{pending.deviceUid}</b>
          {pending.ipAddress ? ` (IP ${pending.ipAddress})` : ''} และ<b>เริ่มรับข้อมูลจากอุปกรณ์นี้แทน</b>
          <br />
          อุปกรณ์เดิม (<b>{board.deviceUid ?? '-'}</b>) จะถูกปฏิเสธแทน — ทำต่อเมื่อแน่ใจว่าเป็นการเปลี่ยนตัวจริง
        </div>
      ),
      okText: 'ยอมรับและเปลี่ยน',
      cancelText: 'ยกเลิก',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await acceptUidMut.mutateAsync(id);
          showToast(`ผูกบอร์ด ${board.code} กับอุปกรณ์ ${pending.deviceUid} แล้ว`);
        } catch (err) {
          const msg =
            (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
            (err as Error).message ?? 'ยอมรับไม่สำเร็จ';
          showToast(msg, 'error');
          throw err;
        }
      },
    });
  };

  // Ignore: keep the real device, just silence this bogus UID forever. No
  // confirm modal needed — it's non-destructive (drops the intruder, not us).
  const handleIgnoreUid = async () => {
    if (!pending || id == null) return;
    try {
      await ignoreUidMut.mutateAsync(id);
      showToast(`เพิกเฉยอุปกรณ์ ${pending.deviceUid} — จะไม่เตือนซ้ำ`, 'info');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        (err as Error).message ?? 'ทำรายการไม่สำเร็จ';
      showToast(msg, 'error');
    }
  };

  return (
    <div>
      <PageHeader
        title={`รายละเอียดบอร์ด · ${board.code}`}
        breadcrumb="จัดการอุปกรณ์ → รายละเอียดบอร์ด"
        icon={Cpu}
      />

      {/* Top bar: back button · always-visible status chips · edit/delete.
          Keeping the essential status inline here means it stays on screen on
          every tab without eating a whole card row. */}
      <div style={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          <button
            onClick={() => navigate('/admin/devices')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--bg-input)', border: '1px solid var(--border-color)',
              color: 'var(--text)', padding: '7px 14px', borderRadius: 8,
              cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
            }}
          >
            <ArrowLeft size={14} />
            กลับ
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', flex: 1, minWidth: 0, fontSize: 13 }}>
          <button
            onClick={() => setDiagOpen(true)}
            title="คลิกเพื่อดูสาเหตุ/รายละเอียดสถานะ"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none',
              border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
            }}
          >
            <StatusChip color={BOARD_STATUS_META[boardConnStatus].color} label={BOARD_STATUS_META[boardConnStatus].label} strong />
            <Info size={13} style={{ color: 'var(--dim)' }} />
          </button>
          {/* link + uptime are live-only readings — when the board is offline
              the cached values are stale, so hide them (uptime especially must
              not keep "counting" from a frozen number). */}
          {showLive && isOnline && <StatusChip color={linkColor} label={`${linkLabel}${linkDetail ? ' · ' + linkDetail : ''}`} />}
          {showLive && <StatusChip color={mqttMeta.color} label={`MQTT ${mqttMeta.label}`} />}
          {showLive && isOnline && <StatusChip color="#94a3b8" label={`Uptime ${fmtUptime(live!.uptimeMs)}`} />}
          <StatusChip
            color="#a78bfa"
            label={`${sensors.length} เซนเซอร์${sensors.length ? ` (${[hasPzem ? 'PZEM' : null, hasKws ? 'KWS' : null].filter(Boolean).join(' + ')})` : ''}`}
          />
          <span style={{ color: 'var(--dim)', fontSize: 12 }}>
            ข้อมูลล่าสุด: {newestActivity > 0 ? dayjs(newestActivity).fromNow() : 'ยังไม่เคยส่งข้อมูล'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
          <button
            onClick={() => setBoardEditOpen(true)}
            title="แก้ไขข้อมูลบอร์ด (ชื่อ / โซน / IP / ฯลฯ)"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--bg-input)', border: '1px solid var(--border-color)',
              color: 'var(--text)', padding: '8px 14px', borderRadius: 8,
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: 13,
            }}
          >
            <Pencil size={14} />
            แก้ไขบอร์ด
          </button>
          <button
            onClick={() => setBoardDeleting(true)}
            title="ลบบอร์ด"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: '1px solid var(--red)',
              color: 'var(--red)', padding: '8px 14px', borderRadius: 8,
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: 13,
            }}
          >
            <Trash2 size={14} />
            ลบ
          </button>
        </div>
      </div>

      {/* Duplicate-board_code warning. A foreign device is publishing under this
          board's code; its data is QUARANTINED (dropped) until an admin accepts
          it here. */}
      {pending && (
        <div
          style={{
            display: 'flex', alignItems: isMobile ? 'stretch' : 'center',
            flexDirection: isMobile ? 'column' : 'row', gap: 14,
            background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.45)',
            borderRadius: 10, padding: '14px 16px', marginBottom: 18,
          }}
        >
          <AlertTriangle size={22} color="#f59e0b" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.7, color: 'var(--text)' }}>
            <b style={{ color: '#f59e0b' }}>พบอุปกรณ์แปลกปลอมใช้รหัสบอร์ดซ้ำ</b>
            <br />
            มีอุปกรณ์ <b>{pending.deviceUid}</b>
            {pending.ipAddress ? ` (IP ${pending.ipAddress})` : ''}
            {pending.firmware ? ` · ${pending.firmware}` : ''} ส่งข้อมูลมาด้วยรหัส <b>{board.code}</b>{' '}
            ซึ่งผูกไว้กับ <b>{board.deviceUid ?? '-'}</b> อยู่แล้ว —{' '}
            <b>ข้อมูลจากอุปกรณ์ใหม่ถูกปฏิเสธทั้งหมด</b> (ไม่นำมาคำนวณ/แสดง) —{' '}
            <b>ยอมรับ</b> = เปลี่ยนไปผูกอุปกรณ์ใหม่ · <b>เพิกเฉย</b> = คงอุปกรณ์เดิม ไม่เตือนอุปกรณ์นี้อีก
            <span style={{ color: 'var(--dim)' }}> · พบล่าสุด {dayjs(pending.at).fromNow()}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
            <button
              onClick={handleAcceptUid}
              disabled={acceptUidMut.isPending}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: '#f59e0b', border: 'none', color: '#1a1205',
                padding: '9px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                cursor: acceptUidMut.isPending ? 'wait' : 'pointer', fontFamily: 'inherit',
              }}
            >
              <AlertTriangle size={14} />
              ยอมรับอุปกรณ์นี้
            </button>
            <button
              onClick={handleIgnoreUid}
              disabled={ignoreUidMut.isPending}
              title="คงอุปกรณ์เดิมไว้ และเลิกเตือนอุปกรณ์แปลกปลอมตัวนี้ (ข้อมูลยังถูกทิ้งเหมือนเดิม)"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'transparent', border: '1px solid var(--border-color)',
                color: 'var(--dim)', padding: '9px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13,
                cursor: ignoreUidMut.isPending ? 'wait' : 'pointer', fontFamily: 'inherit',
              }}
            >
              <BellOff size={14} />
              เพิกเฉย
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
        {([
          ['info', 'ข้อมูลบอร์ด', Cpu],
          ['energy', 'เซนเซอร์ & พลังงาน', Gauge],
          ...(relayCapable ? ([['control', 'การควบคุม', Power]] as const) : []),
          ['activity', 'ประวัติบอร์ด', ScrollText],
        ] as const).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '10px 18px', background: 'none', border: 'none',
              borderBottom: showTab === key ? '2px solid var(--cyan)' : '2px solid transparent',
              color: showTab === key ? 'var(--text)' : 'var(--dim)',
              fontWeight: showTab === key ? 700 : 500,
              fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', marginBottom: -1,
            }}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {showTab === 'energy' && (
      <>
      {/* Sensors header + add */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--dim)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Wifi size={14} />
          เซ็นเซอร์ที่ต่อกับบอร์ด ({sensors.length})
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          disabled={slotsFull}
          title={slotsFull ? 'บอร์ดนี้มี PZEM + KWS ครบแล้ว' : 'เพิ่มเซ็นเซอร์ใหม่'}
          style={{
            padding: '9px 16px',
            background: slotsFull ? 'var(--bg-input)' : 'var(--cyan)',
            color: slotsFull ? 'var(--dim2)' : '#fff',
            border: slotsFull ? '1px solid var(--border-color)' : 'none',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 13,
            cursor: slotsFull ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            opacity: slotsFull ? 0.6 : 1,
          }}
        >
          <Plus size={15} />
          {slotsFull ? 'เต็มแล้ว (PZEM + KWS)' : 'เพิ่มเซ็นเซอร์'}
        </button>
      </div>

      {/* Sensor aggregation mode — only relevant when a board carries 2+ meters.
          Decides whether site/zone energy totals count only the primary meter
          (two meters on the SAME circuit) or every sensor (DIFFERENT loads). */}
      {sensors.length >= 2 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14, padding: '12px 14px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: 10 }}>
          <div style={{ fontSize: 12.5, color: 'var(--dim)', minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>การนับเซนเซอร์ในยอดรวมพลังงาน</div>
            บอร์ดนี้มี {sensors.length} เซนเซอร์ — เลือกว่ารวมเข้ายอดไซต์/โซนอย่างไร
          </div>
          <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4, background: 'var(--bg-card)', padding: 3, borderRadius: 9, border: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
            {([
              ['primary', 'นับเซนเซอร์หลัก'],
              ['independent', 'นับทุกเซนเซอร์'],
            ] as const).map(([mode, label]) => {
              const active = (board.sensorAggMode ?? 'primary') === mode;
              return (
                <button
                  key={mode}
                  disabled={updateBoardMut.isPending || active}
                  onClick={() =>
                    modal.confirm({
                      title: 'เปลี่ยนวิธีนับเซนเซอร์?',
                      content: mode === 'independent'
                        ? 'นับทุกเซนเซอร์ของบอร์ดนี้แยกอิสระ (เหมาะสำหรับการแยกโหลด) — ยอดรวมพลังงาน/กำลังไฟของไซต์จะเพิ่มขึ้นตามจำนวนเซนเซอร์'
                        : 'นับเฉพาะเซนเซอร์หลักเท่านั้น — ยอดรวมพลังงาน/กำลังไฟของไซต์ จะนับจากเซนเซอร์หลักเท่านั้น',
                      okText: 'ยืนยันเปลี่ยน',
                      cancelText: 'ยกเลิก',
                      centered: true,
                      onOk: () =>
                        updateBoardMut.mutateAsync({ id: board.id, payload: { sensorAggMode: mode } })
                          .then(() => showToast('อัปเดตการนับเซนเซอร์แล้ว', 'info'))
                          .catch(() => showToast('บันทึกไม่สำเร็จ', 'error')),
                    })
                  }
                  style={{
                    padding: '6px 12px', borderRadius: 7, border: 'none', fontFamily: 'inherit',
                    fontSize: 12, fontWeight: active ? 700 : 500,
                    cursor: active ? 'default' : 'pointer',
                    background: active ? 'var(--cyan)' : 'transparent',
                    color: active ? '#001' : 'var(--dim)',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
          gap: 16,
        }}
      >
        {sensors.map((s) => (
          <SensorCard
            key={s.id}
            sensor={s}
            reading={latestBySensor.get(s.id)}
            onEdit={() => {
              setEditing(s);
              setModalOpen(true);
            }}
            onToggle={() => handleToggle(s)}
            onDelete={() => setDeletingSensor(s)}
          />
        ))}
        {sensors.length === 0 && (
          <div
            style={{
              padding: 28,
              background: 'var(--bg-card)',
              border: '1px dashed var(--border-color)',
              borderRadius: 12,
              textAlign: 'center',
              color: 'var(--dim)',
              gridColumn: '1 / -1',
            }}
          >
            ยังไม่มีเซ็นเซอร์ — กดปุ่ม "เพิ่มเซ็นเซอร์" ด้านบนเพื่อเริ่มต้น
          </div>
        )}
      </div>

      {/* Energy history now lives inside the sensor tab (merged). */}
      <div style={{ marginTop: 18 }}>
        <EnergyHistory siteId={board.siteId} sensors={sensors} />
      </div>

      </>
      )}

      {showTab === 'control' && <RelayPanel boardId={board.id} />}

      {showTab === 'activity' && <ActivityHistory boardId={board.id} />}

      {showTab === 'info' && (
        <section className="card fade-in-up" style={{ padding: 22 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <FieldGroup title="ข้อมูลบอร์ด">
              <InfoField label="รหัสบอร์ด" value={board.code} />
              <InfoField label="ชื่อ" value={board.name ?? '-'} />
              <InfoField label="ฮาร์ดแวร์" value={board.hardware ?? '-'} />
              <InfoField label="Firmware" value={board.firmware ?? '-'} />
              <InfoField label="ไซต์" value={`${board.siteCode ?? '-'}${board.siteName ? ' · ' + board.siteName : ''}`} />
              <InfoField
                label="โซน"
                value={board.zoneName || board.zoneCode ? `${board.zoneCode ?? ''}${board.zoneName ? ' · ' + board.zoneName : ''}` : 'ไม่ระบุ'}
              />
            </FieldGroup>

            <FieldGroup title="เครือข่าย">
              {showLive && (
                <InfoField
                  label="ลิงก์ที่ใช้"
                  value={<span style={{ color: linkColor, fontWeight: 600 }}>{linkLabel}{linkDetail ? ` · ${linkDetail}` : ''}</span>}
                />
              )}
              {showLive && (
                <InfoField
                  label="MQTT"
                  value={<span style={{ color: mqttMeta.color, fontWeight: 600 }}>{mqttMeta.label}</span>}
                />
              )}
              <InfoField label="IP Address" value={board.ipAddress ?? '-'} mono />
              <InfoField label="MAC" value={board.macAddress ?? '-'} mono />
              <InfoField label="Device UID" value={live?.deviceUid ?? '-'} mono />
            </FieldGroup>

            <FieldGroup title="สถานะ & กิจกรรม">
              <InfoField
                label="สถานะ"
                value={<BoardStatusBadge status={boardConnStatus} />}
              />
              {showLive && <InfoField label="Uptime" value={isOnline ? fmtUptime(live!.uptimeMs) : '—'} />}
              <InfoField
                label="เซ็นเซอร์"
                value={`${sensors.length} ตัว${
                  sensors.length ? ` (${[hasPzem ? 'PZEM' : null, hasKws ? 'KWS' : null].filter(Boolean).join(' + ')})` : ''
                }`}
              />
              <InfoField
                label="ข้อมูลล่าสุด"
                value={
                  newestActivity > 0
                    ? `${dayjs(newestActivity).fromNow()} · ${dayjs(newestActivity).format('DD/MM/YYYY HH:mm:ss')}`
                    : 'ยังไม่เคยส่งข้อมูล'
                }
              />
            </FieldGroup>
          </div>
        </section>
      )}

      <SensorFormModal
        open={modalOpen}
        boardId={board.id}
        editing={editing}
        existingSensors={sensors}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        submitting={createMut.isPending || updateMut.isPending}
      />

      <BoardFormModal
        open={boardEditOpen}
        editing={board as BoardWithSensors}
        sites={sites}
        onClose={() => setBoardEditOpen(false)}
        onSubmit={async (payload) => {
          try {
            await updateBoardMut.mutateAsync({
              id: board.id, payload: payload as UpdateBoardPayload,
            });
            showToast(`อัปเดต ${board.code} เรียบร้อย`);
            setBoardEditOpen(false);
          } catch (err) {
            showToast((err as Error).message ?? 'บันทึกไม่สำเร็จ', 'error');
          }
        }}
        submitting={updateBoardMut.isPending}
      />

      <BoardDiagnosisModal
        open={diagOpen}
        onClose={() => setDiagOpen(false)}
        boardCode={board.code}
        diagnosis={diagnosis}
        facts={diagFacts}
      />

      {boardDeleting && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 1000, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => e.target === e.currentTarget && setBoardDeleting(false)}
        >
          <div
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-color)',
              borderRadius: 14, padding: 28, width: 420,
              textAlign: 'center', boxShadow: 'var(--shadow-lg)',
            }}
          >
            <Trash2 size={48} color="var(--red)" style={{ marginBottom: 12 }} />
            <h3 style={{ color: 'var(--text)', marginBottom: 8 }}>
              ยืนยันการลบบอร์ด
            </h3>
            <p style={{ color: 'var(--dim)', fontSize: 14 }}>
              ลบบอร์ด <strong style={{ color: 'var(--red)' }}>{board.code}</strong>?
              <br />
              {sensors.length > 0 && (
                <span style={{ color: 'var(--yellow)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <AlertTriangle size={13} /> ต้องลบ sensor {sensors.length} ตัวก่อน
                </span>
              )}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 22 }}>
              <button
                onClick={() => setBoardDeleting(false)}
                style={{
                  padding: '9px 18px',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text)',
                  borderRadius: 8, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >ยกเลิก</button>
              <button
                onClick={async () => {
                  try {
                    await deleteBoardMut.mutateAsync(board.id);
                    showToast(`ลบ ${board.code} แล้ว`, 'info');
                    navigate('/admin/devices');
                  } catch (err) {
                    const msg = (err as { response?: { data?: { message?: string } } })
                      .response?.data?.message ?? 'ลบไม่สำเร็จ (อาจมี sensor ผูกอยู่)';
                    showToast(msg, 'error');
                    setBoardDeleting(false);
                  }
                }}
                style={{
                  padding: '9px 18px',
                  background: 'var(--red)', border: 'none', color: '#fff',
                  borderRadius: 8, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >ลบเลย</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deletingSensor && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={(e) => e.target === e.currentTarget && setDeletingSensor(null)}
        >
          <div
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-color)',
              borderRadius: 14,
              padding: 28,
              width: 420,
              textAlign: 'center',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <Trash2 size={48} color="#ef4444" style={{ marginBottom: 12 }} />
            <h3 style={{ color: 'var(--text)', marginBottom: 8 }}>ยืนยันการลบ</h3>
            <p style={{ color: 'var(--dim)', fontSize: 14 }}>
              ลบเซ็นเซอร์{' '}
              <strong style={{ color: 'var(--red)' }}>{deletingSensor.code}</strong>
              ?<br />
              ข้อมูล telemetry เก่าจะยังอยู่ใน DB
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 22 }}>
              <button
                onClick={() => setDeletingSensor(null)}
                style={{
                  padding: '9px 18px',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text)',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                ยกเลิก
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: '9px 18px',
                  background: 'var(--red)',
                  border: 'none',
                  color: '#fff',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                ลบเลย
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// Relay control requires firmware v0.16+ (the multi-relay system). Older boards
// don't expose it, so the control tab is hidden for them.
function fwSupportsRelays(fw?: string | null): boolean {
  const m = /v?(\d+)\.(\d+)/.exec(fw ?? '');
  if (!m) return false;
  const major = Number(m[1]);
  const minor = Number(m[2]);
  return major > 0 || (major === 0 && minor >= 16);
}

function fmtUptime(ms?: number): string {
  if (ms == null || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function StatusChip({ color, label, strong }: { color: string; label: string; strong?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: strong ? 700 : 500, color: strong ? color : 'var(--text)', whiteSpace: 'nowrap' }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, boxShadow: `0 0 7px ${color}`, flexShrink: 0 }} />
      {label}
    </span>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  const isMobile = useIsMobile();
  return (
    <div>
      <div
        style={{
          fontSize: 11, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase',
          letterSpacing: '0.06em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10,
        }}
      >
        {title}
        <span style={{ flex: 1, height: 1, background: 'var(--border-color)' }} />
      </div>
      <div
        style={{
          display: 'grid',
          // Two columns on desktop, one on phone.
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          gap: isMobile ? '12px 0' : '14px 40px',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function InfoField({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--dim2)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: 4,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          color: 'var(--text)',
          fontFamily: mono ? 'monospace' : 'inherit',
          fontWeight: mono ? 500 : 400,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// Safe numeric read from a telemetry point's `raw` bag (AC306L extras live
// there: apparentPower, reactivePower, reactiveEnergy, pfA/pfB/pfC, ...).
function rawNum(reading: TelemetryPoint | undefined, key: string): number | null {
  const v = (reading?.raw as Record<string, unknown> | undefined)?.[key];
  return typeof v === 'number' ? v : null;
}

function SensorCard({
  sensor,
  reading,
  onEdit,
  onToggle,
  onDelete,
}: {
  sensor: SensorWithContext;
  reading?: TelemetryPoint;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const hasData = !!reading;
  const stale = reading && dayjs().diff(reading.time, 'second') > 30;
  const showLive = sensor.isActive && hasData && !stale;

  const color = sensor.code.startsWith('PZEM')
    ? '#06b6d4'
    : sensor.code.startsWith('KWS')
      ? '#a78bfa'
      : '#22c55e';

  const opacity = sensor.isActive ? 1 : 0.55;

  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 12,
        padding: 18,
        boxShadow: 'var(--shadow-sm)',
        opacity,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 14,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: 'var(--text)',
              letterSpacing: '-0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {sensor.code}
            {sensor.isPrimary && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: 'rgba(34, 211, 238, 0.15)',
                  color: 'var(--cyan)',
                  border: '1px solid rgba(34, 211, 238, 0.4)',
                }}
                title="เซ็นเซอร์หลัก (ใช้คำนวณพลังงานรวมของบอร์ด)"
              >
                หลัก
              </span>
            )}
            {!sensor.isActive && (
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 7px',
                  borderRadius: 999,
                  background: 'rgba(148, 163, 184, 0.15)',
                  color: 'var(--dim2)',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                }}
              >
                ปิดใช้งาน
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 2 }}>
            {sensor.name ?? 'ไม่มีชื่อ'}{' '}
            {sensor.model && <span style={{ color: 'var(--dim2)' }}>· {sensor.model}</span>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {sensor.isActive && (
            <span
              style={{
                padding: '3px 10px',
                borderRadius: 999,
                background: showLive
                  ? 'rgba(34, 197, 94, 0.12)'
                  : 'rgba(148, 163, 184, 0.12)',
                color: showLive ? '#22c55e' : 'var(--dim2)',
                fontSize: 11,
                fontWeight: 700,
                border: `1px solid ${showLive ? '#22c55e33' : 'var(--border-color)'}`,
                letterSpacing: '0.04em',
                marginRight: 4,
              }}
            >
              {showLive ? 'LIVE' : 'ไม่มีข้อมูล'}
            </span>
          )}
          <IconBtn
            title={sensor.isActive ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
            onClick={onToggle}
            color={sensor.isActive ? '#22c55e' : '#94a3b8'}
          >
            <Power size={14} />
          </IconBtn>
          <IconBtn title="แก้ไข" onClick={onEdit}>
            <Pencil size={14} />
          </IconBtn>
          <IconBtn title="ลบ" onClick={onDelete} color="#ef4444">
            <Trash2 size={14} />
          </IconBtn>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
          gap: 12,
          marginBottom: 12,
        }}
      >
        {/* Fixed order: Voltage · Current · Power · Energy · Frequency ·
            Temperature. Frequency + Temperature hide when the sensor has no
            such value. (Apparent/Reactive/Reactive-E are still stored in raw
            but intentionally not displayed.) */}
        <Metric Icon={Zap} color="#06b6d4" label="Voltage" value={reading?.voltage} unit="V" digits={1} />
        <Metric Icon={Activity} color="#22c55e" label="Current" value={reading?.current} unit="A" digits={3} />
        <Metric Icon={Gauge} color="#a78bfa" label="Power" value={reading?.power} unit="W" digits={1} />
        <Metric Icon={BatteryCharging} color="#f59e0b" label="Energy" value={reading?.energy} unit="kWh" digits={3} />
        {rawNum(reading, 'frequency') != null && (
          <Metric Icon={Hash} color="#94a3b8" label="Frequency" value={rawNum(reading, 'frequency')} unit="Hz" digits={1} />
        )}
        {reading?.temperature != null && (
          <Metric Icon={Thermometer} color="#ef4444" label="Temperature" value={reading.temperature} unit="°C" digits={1} />
        )}
      </div>

      {/* Per-phase breakdown for KWS-AC306L 3-phase sensors. The main
          grid above shows aggregates (V_A as the reference, sum of
          currents, meter Total power); this strip splits them per
          phase so an unbalanced load is visible at a glance. */}
      <PerPhaseStrip sensor={sensor} raw={reading?.raw} />

      {/* Peak Limit (per-sensor thresholds) */}
      {(sensor.voltageMin != null ||
        sensor.voltageMax != null ||
        sensor.currentMax != null ||
        sensor.powerMax != null ||
        sensor.temperatureMax != null) && (
        <div
          style={{
            fontSize: 10.5,
            color: 'var(--dim)',
            paddingTop: 10,
            borderTop: '1px dashed var(--border-color)',
            marginBottom: 8,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}
        >
          <span
            style={{
              fontWeight: 700,
              letterSpacing: '0.04em',
              color: 'var(--dim2)',
            }}
          >
            PEAK LIMITS:
          </span>
          {sensor.voltageMin != null && (
            <LimitBadge label={`V ≥ ${sensor.voltageMin}V`} />
          )}
          {sensor.voltageMax != null && (
            <LimitBadge label={`V ≤ ${sensor.voltageMax}V`} />
          )}
          {sensor.currentMax != null && (
            <LimitBadge label={`I ≤ ${sensor.currentMax}A`} />
          )}
          {sensor.powerMax != null && (
            <LimitBadge label={`W ≤ ${sensor.powerMax}W`} />
          )}
          {sensor.temperatureMax != null && (
            <LimitBadge label={`T ≤ ${sensor.temperatureMax}°C`} />
          )}
        </div>
      )}

      <div
        style={{
          fontSize: 11,
          color: 'var(--dim2)',
          paddingTop: 10,
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>
          {sensor.channel ? `ช่อง: ${sensor.channel}` : ''}
          {sensor.channel && reading && ' · '}
          {reading && `อัปเดต: ${dayjs(reading.time).fromNow()}`}
        </span>
        {reading && (reading.raw as Record<string, number> | undefined)?.pf != null && (
          <span>PF: {(reading.raw as Record<string, number>).pf.toFixed(2)}</span>
        )}
      </div>
    </div>
  );
}

// Only render anything when the sensor identifies as KWS 3-phase
// (model contains AC306) AND the firmware has supplied per-phase
// values in the raw payload. Otherwise this returns null and the
// surrounding layout collapses cleanly.
function PerPhaseStrip({
  sensor,
  raw,
}: {
  sensor: { model?: string | null; phases?: 1 | 3 | null };
  raw: unknown;
}) {
  // Per-phase strip only renders for KWS-AC306L 3-phase sensors —
  // 1-phase hardware has nothing meaningful to break out. `phases`
  // is the authoritative signal; the model-string fallback handles
  // legacy rows that pre-date the column.
  const m = (sensor.model ?? '').toUpperCase();
  const isThreePhase = sensor.phases === 3
    || (sensor.phases == null && (m.includes('AC306') || m.includes('3P')));
  if (!isThreePhase) return null;
  const r = raw as Record<string, unknown> | undefined;
  if (!r) return null;
  // Skip if firmware predates per-phase fields entirely — don't render
  // a misleading "Phase A: 0/0/0" panel for boards on old firmware.
  const hasPerPhase =
    r.vA !== undefined || r.iA !== undefined || r.pA !== undefined;
  if (!hasPerPhase) return null;
  const num = (k: string) => (typeof r[k] === 'number' ? (r[k] as number) : 0);
  // Per-phase PF is an AC306L extra — only render the column when present.
  const hasPf = typeof r.pfA === 'number';
  const phases = [
    { label: 'A', v: num('vA'), i: num('iA'), p: num('pA'), pf: num('pfA') },
    { label: 'B', v: num('vB'), i: num('iB'), p: num('pB'), pf: num('pfB') },
    { label: 'C', v: num('vC'), i: num('iC'), p: num('pC'), pf: num('pfC') },
  ];
  return (
    <div
      style={{
        marginTop: 2,
        marginBottom: 12,
        padding: '10px 12px',
        background: 'var(--bg-input)',
        border: '1px solid var(--border-color)',
        borderRadius: 8,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <div style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em',
        color: 'var(--dim2)', textTransform: 'uppercase', marginBottom: 6,
      }}>
        Per-phase
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: hasPf ? '24px 1fr 1fr 1fr 1fr' : '24px 1fr 1fr 1fr',
        gap: '4px 14px',
        fontSize: 12,
        color: 'var(--text)',
      }}>
        <span style={{ color: 'var(--dim)', fontWeight: 600 }} />
        <span style={{ color: 'var(--dim)', fontWeight: 600 }}>V</span>
        <span style={{ color: 'var(--dim)', fontWeight: 600 }}>A</span>
        <span style={{ color: 'var(--dim)', fontWeight: 600 }}>W</span>
        {hasPf && <span style={{ color: 'var(--dim)', fontWeight: 600 }}>PF</span>}
        {phases.map((p) => (
          <Fragment key={p.label}>
            <span style={{ fontWeight: 700, color: 'var(--cyan)' }}>{p.label}</span>
            <span>{p.v.toFixed(1)}</span>
            <span>{p.i.toFixed(3)}</span>
            <span>{p.p.toFixed(1)}</span>
            {hasPf && <span>{p.pf.toFixed(2)}</span>}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function LimitBadge({ label }: { label: string }) {
  return (
    <span
      style={{
        padding: '1px 7px',
        borderRadius: 4,
        background: 'rgba(167, 139, 250, 0.1)',
        color: '#a78bfa',
        border: '1px solid rgba(167, 139, 250, 0.25)',
        fontSize: 10,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {label}
    </span>
  );
}

function IconBtn({
  children,
  onClick,
  title,
  color,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  color?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: 'var(--bg-input)',
        border: '1px solid var(--border-color)',
        color: color ?? 'var(--text)',
        width: 30,
        height: 30,
        borderRadius: 7,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

function Metric({
  Icon,
  color,
  label,
  value,
  unit,
  digits,
}: {
  Icon: LucideIcon;
  color: string;
  label: string;
  value: number | null | undefined;
  unit: string;
  digits: number;
}) {
  return (
    <div
      style={{
        padding: '10px 12px',
        background: 'var(--bg-input)',
        borderRadius: 8,
        border: '1px solid var(--border-color)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--dim)',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          marginBottom: 4,
          fontWeight: 600,
          letterSpacing: '0.03em',
        }}
      >
        <Icon size={11} color={color} />
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: value != null ? 'var(--text)' : 'var(--dim2)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value != null ? value.toFixed(digits) : '—'}
        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--dim)', marginLeft: 4 }}>
          {unit}
        </span>
      </div>
    </div>
  );
}
